import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowUpRight } from "@phosphor-icons/react";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const revealUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.9, ease: [0.7, 0, 0.2, 1] },
  }),
};

const gridSpans = [
  "md:col-span-7 md:row-span-2 aspect-[4/5] md:aspect-auto",
  "md:col-span-5 aspect-[4/3]",
  "md:col-span-5 aspect-[4/3]",
  "md:col-span-4 aspect-[3/4]",
  "md:col-span-4 aspect-[3/4]",
  "md:col-span-4 aspect-[3/4]",
];

export const Portfolio = () => {
  const { t } = useLang();
  const [items, setItems] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/properties`)
      .then((res) => setItems(res.data))
      .catch(() => setItems([]));
  }, []);

  return (
    <section
      id="portfolio"
      className="relative bg-[#F1EEE8] text-[#232326] py-28 md:py-40"
      data-testid="portfolio-section"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-8 mb-16 md:mb-24">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={revealUp}
            className="md:col-span-3"
          >
            <div className="flex items-center gap-3 text-[#13535A] text-[0.7rem] tracking-[0.28em] uppercase font-semibold">
              <span className="w-6 h-px bg-[#8A6F45]" />
              <span>{t.portfolio.overline}</span>
            </div>
          </motion.div>

          <div className="md:col-span-9">
            <motion.h2
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={revealUp}
              custom={1}
              className="font-display font-medium leading-[1.02] tracking-tight text-[clamp(2.2rem,5.4vw,4.8rem)] whitespace-pre-line mb-8"
            >
              {t.portfolio.title}
            </motion.h2>
            <motion.p
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={revealUp}
              custom={2}
              className="text-[#232326]/70 max-w-xl leading-relaxed text-[1rem]"
            >
              {t.portfolio.subtitle}
            </motion.p>
          </div>
        </div>

        <div className="grid md:grid-cols-12 gap-6 md:gap-8">
          {items.map((p, i) => (
            <a
              href="#contact"
              key={p.id}
              className={`group relative block overflow-hidden clip-card bg-[#E6E3DC] transition-transform duration-500 ease-out hover:scale-[1.03] ${gridSpans[i] || "md:col-span-4 aspect-[3/4]"}`}
              data-testid={`portfolio-${p.slug}`}
            >
              <img
                src={`${p.image}?auto=format&fit=crop&w=1400&q=80`}
                alt={p.name}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B]/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{
                background: "radial-gradient(circle at 50% 40%, rgba(205,170,150,0.18), transparent 60%)"
              }}/>
            </a>
          ))}
        </div>

        <div className="mt-16 md:mt-20 flex justify-end">
          <a href="https://zitroapartments.italianway.house/apartments" target="_blank" rel="noreferrer" className="btn-ghost" data-testid="portfolio-view-all">
            {t.portfolio.viewAll}
            <ArrowUpRight size={14} weight="light" />
          </a>
        </div>
      </div>
    </section>
  );
};
